/**
 * Scene Image Generation Hooks
 * Feature: Generate First Frames for Instagram Reels
 *
 * Provides hooks for:
 * 1. Batch image generation (useSceneImageGeneration)
 * 2. Individual scene image operations (useSingleSceneImage)
 */

import { useReducer, useCallback } from 'react';
import { useAction } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  ISceneImageGenerationState,
  ISingleSceneImageState,
  SceneImageGenerationAction,
  SingleSceneImageAction,
  IUseSceneImageGenerationReturn,
  IUseSingleSceneImageReturn,
} from '../@types/sceneImageGeneration';

// ============================================================================
// Batch Image Generation State Management
// ============================================================================

/**
 * Initial state for batch image generation
 */
const initialBatchState: ISceneImageGenerationState = {
  phase: 'idle',
  progress: {
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: 0,
  },
  results: [],
  error: null,
  errorCode: null,
};

/**
 * Batch Image Generation Reducer
 * Handles all state transitions for batch image generation
 */
function batchGenerationReducer(
  state: ISceneImageGenerationState,
  action: SceneImageGenerationAction
): ISceneImageGenerationState {
  switch (action.type) {
    case 'START_BATCH_GENERATION':
      return {
        ...state,
        phase: 'preparing',
        progress: {
          total: action.payload.totalScenes,
          completed: 0,
          failed: 0,
          inProgress: action.payload.totalScenes,
        },
        results: [],
        error: null,
        errorCode: null,
      };

    case 'SCENE_STARTED':
      return {
        ...state,
        phase: 'generating',
      };

    case 'SCENE_COMPLETED':
      return {
        ...state,
        progress: {
          ...state.progress,
          completed: state.progress.completed + 1,
          inProgress: state.progress.inProgress - 1,
        },
        results: [...state.results, action.payload],
      };

    case 'SCENE_FAILED':
      return {
        ...state,
        progress: {
          ...state.progress,
          failed: state.progress.failed + 1,
          inProgress: state.progress.inProgress - 1,
        },
        results: [...state.results, action.payload],
      };

    case 'BATCH_COMPLETED': {
      const hasFailures = action.payload.failed > 0;
      return {
        ...state,
        phase: hasFailures ? 'partial_success' : 'completed',
        progress: {
          ...state.progress,
          completed: action.payload.succeeded,
          failed: action.payload.failed,
          inProgress: 0,
        },
      };
    }

    case 'BATCH_FAILED':
      return {
        ...state,
        phase: 'failed',
        error: action.payload.error,
        errorCode: action.payload.errorCode || null,
        progress: {
          ...state.progress,
          inProgress: 0,
        },
      };

    case 'RESET':
      return initialBatchState;

    default:
      return state;
  }
}

// ============================================================================
// Single Scene Image State Management
// ============================================================================

/**
 * Initial state for single scene image operations
 */
const initialSingleState: ISingleSceneImageState = {
  sceneId: null,
  frame: null,
  status: 'pending',
  error: null,
  isEditingPrompt: false,
  editedPrompt: '',
};

/**
 * Single Scene Image Reducer
 * Handles state transitions for individual scene image operations
 */
function singleSceneReducer(
  state: ISingleSceneImageState,
  action: SingleSceneImageAction
): ISingleSceneImageState {
  switch (action.type) {
    case 'START_EDIT_PROMPT':
      return {
        ...state,
        sceneId: action.payload.sceneId,
        isEditingPrompt: true,
        editedPrompt: action.payload.currentPrompt,
        error: null,
      };

    case 'UPDATE_PROMPT':
      return {
        ...state,
        editedPrompt: action.payload.prompt,
      };

    case 'CANCEL_EDIT':
      return {
        ...state,
        isEditingPrompt: false,
        editedPrompt: '',
        sceneId: null,
      };

    case 'START_REGENERATION':
      return {
        ...state,
        sceneId: action.payload.sceneId,
        frame: action.payload.frame,
        status: 'generating',
        isEditingPrompt: false,
        error: null,
      };

    case 'REGENERATION_COMPLETED':
      return {
        ...state,
        status: 'completed',
        sceneId: null,
        frame: null,
        editedPrompt: '',
      };

    case 'REGENERATION_FAILED':
      return {
        ...state,
        status: 'failed',
        error: action.payload.error,
      };

    case 'RESET':
      return initialSingleState;

    default:
      return state;
  }
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * useSceneImageGeneration Hook
 * Manages batch image generation for all scenes in a reel
 *
 * Features:
 * - Parallel generation of all scene images
 * - Progress tracking with live updates
 * - Partial success handling
 * - Toast notifications for success/failure
 */
export function useSceneImageGeneration(
  reelId: Id<'reels'> | undefined,
  totalScenes: number
): IUseSceneImageGenerationReturn {
  const [state, dispatch] = useReducer(batchGenerationReducer, initialBatchState);

  // Action for batch generation
  const generateSceneImagesAction = useAction(api.ai.sceneImageGeneration.generateSceneImages);

  /**
   * Generate images for all scenes
   */
  const generateAllImages = useCallback(async () => {
    if (!reelId) {
      toast.error('No reel selected');
      return;
    }

    if (totalScenes === 0) {
      toast.error('No scenes to generate images for');
      return;
    }

    dispatch({ type: 'START_BATCH_GENERATION', payload: { totalScenes } });

    try {
      const result = await generateSceneImagesAction({ reelId });

      if (result.success) {
        dispatch({
          type: 'BATCH_COMPLETED',
          payload: {
            succeeded: result.succeededCount,
            failed: result.failedCount,
          },
        });

        toast.success(`Generated ${result.succeededCount} images successfully`);
      } else if (result.succeededCount > 0) {
        // Partial success
        dispatch({
          type: 'BATCH_COMPLETED',
          payload: {
            succeeded: result.succeededCount,
            failed: result.failedCount,
          },
        });

        toast.warning(
          `Generated ${result.succeededCount}/${result.totalScenes} images. ${result.failedCount} failed.`
        );
      } else {
        // Complete failure
        dispatch({
          type: 'BATCH_FAILED',
          payload: {
            error: result.error || 'Failed to generate images',
            errorCode: result.errorCode,
          },
        });

        toast.error(result.error || 'Failed to generate images');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      dispatch({
        type: 'BATCH_FAILED',
        payload: { error: errorMessage },
      });
      toast.error(errorMessage);
    }
  }, [reelId, totalScenes, generateSceneImagesAction]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Computed values
  const isGenerating = state.phase === 'preparing' || state.phase === 'generating';
  const canGenerate = !!reelId && totalScenes > 0 && !isGenerating;
  const hasFailedScenes = state.progress.failed > 0;

  return {
    state,
    generateAllImages,
    reset,
    isGenerating,
    canGenerate,
    hasFailedScenes,
    progress: state.progress,
  };
}

/**
 * useSingleSceneImage Hook
 * Manages individual scene image regeneration with prompt editing
 *
 * Features:
 * - Edit image prompt before regenerating
 * - Regenerate with original or custom prompt
 * - Loading states and error handling
 */
export function useSingleSceneImage(): IUseSingleSceneImageReturn {
  const [state, dispatch] = useReducer(singleSceneReducer, initialSingleState);

  // Action for single scene regeneration
  const regenerateSceneImageAction = useAction(api.ai.sceneImageGeneration.regenerateSceneImage);

  /**
   * Regenerate image for a single scene
   */
  const regenerateImage = useCallback(
    async (sceneId: Id<'reelScenes'>, frame: 'first' | 'last', prompt?: string) => {
      dispatch({ type: 'START_REGENERATION', payload: { sceneId, frame } });

      try {
        const result = await regenerateSceneImageAction({
          sceneId,
          customPrompt: prompt,
          frame,
        });

        if (result.success) {
          dispatch({ type: 'REGENERATION_COMPLETED' });
          toast.success('Image regenerated successfully');
        } else {
          dispatch({
            type: 'REGENERATION_FAILED',
            payload: { error: result.error || 'Failed to regenerate image' },
          });
          toast.error(result.error || 'Failed to regenerate image');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'REGENERATION_FAILED',
          payload: { error: errorMessage },
        });
        toast.error(errorMessage);
      }
    },
    [regenerateSceneImageAction]
  );

  /**
   * Start editing the image prompt for a scene
   */
  const startEditPrompt = useCallback((sceneId: Id<'reelScenes'>, currentPrompt: string) => {
    dispatch({
      type: 'START_EDIT_PROMPT',
      payload: { sceneId, currentPrompt },
    });
  }, []);

  /**
   * Update the edited prompt
   */
  const updateEditedPrompt = useCallback((prompt: string) => {
    dispatch({ type: 'UPDATE_PROMPT', payload: { prompt } });
  }, []);

  /**
   * Cancel prompt editing
   */
  const cancelEditPrompt = useCallback(() => {
    dispatch({ type: 'CANCEL_EDIT' });
  }, []);

  /**
   * Confirm edited prompt and regenerate
   */
  const confirmAndRegenerate = useCallback(async () => {
    if (!state.sceneId || !state.frame) {
      toast.error('No scene selected');
      return;
    }

    await regenerateImage(state.sceneId, state.frame, state.editedPrompt || undefined);
  }, [state.sceneId, state.frame, state.editedPrompt, regenerateImage]);

  // Computed values
  const isRegenerating = state.status === 'generating';
  const isEditing = state.isEditingPrompt;

  return {
    state,
    regenerateImage,
    startEditPrompt,
    updateEditedPrompt,
    cancelEditPrompt,
    confirmAndRegenerate,
    isRegenerating,
    isEditing,
  };
}
